import { EventEmitter } from "eventemitter3";
import * as isMobile from "ismobilejs";
import {
    DomElementBase,
    DomElementInteractionEventTypes,
    DomElementImage,
    DomElementTypes,
    DomElementButton,
    DomElementToggle,
    DomElementInteractionEvent
} from "../dom-element";
import {
    DomLayerManifestInterface,
    DomLayerScreenElementInterface,
    DomLayerScreenInterface,
    DomLayerElementInterface,
    DomLayerScreenStateInterface
} from "../config";
import { Subtitles } from "./Subtitles";

export class DomLayer extends EventEmitter {
    public baseUrl: string = "";
    public domDiv: HTMLDivElement;
    private elements: DomElementBase[] = [];
    private manifest: DomLayerManifestInterface;
    private currentScreenName: string;
    private currentElementList: DomLayerScreenElementInterface[] | null;
    private smallerThanBreakpoint: boolean;
    private stateVariables: any = { mute: false, subtitles: false };
    private stateChangeAfterEvent: string | null = null;
    private subtitles: Subtitles;

    public static sortNodes(target: HTMLElement) {
        const sorted: HTMLElement[] = [];
        target.childNodes.forEach(el => sorted.push(el as HTMLElement));
        sorted.sort((a: HTMLElement, b: HTMLElement) => a.tabIndex - b.tabIndex);
        sorted.forEach(el => el.parentNode && el.parentNode.removeChild(el));
        sorted.forEach(el => target.appendChild(el));
    }

    public static isSmallerThanBreakpoint() {
        let smaller = false;
        const w = window.innerWidth;
        const h = window.innerHeight;

        // any screen below 770 wide wil show the small UI
        if (Math.max(w, h) <= 770) {
            smaller = true;
        } else {
            // iPhoneX and Pixel2 are slighly wider than 770 but too shallow to support the larger UI
            const widerBreakpoint = 900; // a wider break point, but not too great, as even letter box layouts which are big enough will support the larger UI
            const ratioBreakpoint = 1.5; // a threshold where phone screens are too shallow to support the lareger UI
            if (isMobile.phone) {
                if (Math.max(w, h) <= widerBreakpoint && w / h >= ratioBreakpoint) {
                    smaller = true;
                }
            }
        }

        return smaller;
    }

    constructor() {
        super();
        this.smallerThanBreakpoint = DomLayer.isSmallerThanBreakpoint();
    }

    public initialise(
        div: HTMLDivElement,
        manifest: DomLayerManifestInterface,
        subtitlesContainer?: HTMLDivElement
    ) {
        this.domDiv = div;
        this.manifest = manifest;
        if (subtitlesContainer) this.createSubtitles(subtitlesContainer);
    }

    /**
     * Displays the specified text in the subtitles, overwritting the current display.
     * @param text - Text to display.
     * @param delay 
     * @param secondary 
     */
    public subtitlesAddText(text: string, delay?: number, secondary?: boolean) {
        this.subtitles.add(text, delay, secondary);
    }

    /**
     * Allows for the display of the subtitles element.
     * @param show - Whether to show or hide the subtitles.
     */
    public subtitlesShow(show?: boolean) {
        if (typeof show === "undefined") show = true;
        this.subtitles.show(show);
    }

    public subtitlesDispose() {
        this.subtitles.dispose();
    }

    public subtitlesReset() {
        this.subtitles.reset();
    }

    public resize(width: number, height: number) {
        if (this.elements && this.elements.length > 0) {
            this.positionAllElements();
            if (this.subtitles) this.subtitles.resize();
        }
    }

    public displayScreen(screenName: string, elementList?: DomLayerScreenElementInterface[]) {
        let screenData: DomLayerScreenInterface | undefined = this.getScreenDataByName(screenName);
        if (screenData) {
            this.destroyAllElements();
            this.currentElementList = elementList ? elementList : null;
            this.currentScreenName = screenName;
            this.createAllElements();
        } else {
            console.warn(`Screen name, ${screenName} is not recognied`);
        }
    }

    public clear() {
        this.destroyAllElements();
    }

    /**
     * Use this if setting a state from the interaction of an element; i.e. before it fires an
     * event. This is used to ensure that the event is fired before rebuilding the elements.
     * @param name
     * @param value
     */
    public elementSetStateVariable(
        name: string,
        value: any,
        eventType: DomElementInteractionEventTypes
    ) {
        if (this.stateVariables.hasOwnProperty(name)) {
            this.stateVariables[name] = value;
            this.stateChangeAfterEvent = name;
        } else {
            console.warn(`State variable name, ${name}, does not exist`);
        }
    }

    /**
     *
     * @param name
     * @param value
     */
    public setStateVariable(name: string, value: any) {
        if (this.stateVariables.hasOwnProperty(name)) {
            this.stateVariables[name] = value;
            this.updateElementStateVariables(name);
        } else {
            console.warn(`State variable name, ${name}, does not exist`);
        }
    }

    private updateElementStateVariables(name: string) {
        if (this.elements) {
            for (let i: number = 0; i < this.elements.length; i++) {
                this.elements[i].updateStateVariable([name, this.stateVariables[name]]);
            }
        }
    }

    /**
     * Recreate the dom layer, so elements can conform to current states.
     */
    private rebuildScreen() {
        if (this.currentElementList) {
            this.destroyAllElements();
            this.createAllFromElementList(this.currentElementList);
        }
    }

    public getStateVariable(name: string) {
        let value: any;
        if (this.stateVariables.hasOwnProperty(name)) {
            value = this.stateVariables[name];
        } else {
            console.warn(`State variable name, ${name}, does not exist`);
        }
        return value;
    }

    /**
     * Unused.
     * Changes the layout of the UI elements to the specified state.
     * If no state is specified, it will revert back to the default screen state.
     * @param state
     */
    public updateScreenState(state?: string | undefined) {
        if (this.currentScreenName) {
            this.destroyAllElements();
            this.createAllElements(state);
        }
    }

    private createSubtitles(subtitlesContainer: HTMLDivElement) {
        this.subtitles = new Subtitles(subtitlesContainer, this.smallerThanBreakpoint);
    }

    private destroyAllElements() {
        if (this.elements) {
            for (let i: number = 0; i < this.elements.length; i++) {
                const domElement: DomElementBase = this.elements[i];

                domElement.off(DomElementInteractionEventTypes.MouseOver);
                domElement.off(DomElementInteractionEventTypes.MouseDown);
                domElement.off(DomElementInteractionEventTypes.MouseUp);
                domElement.off(DomElementInteractionEventTypes.Click);
                domElement.off(DomElementInteractionEventTypes.TouchStart);
                domElement.off(DomElementInteractionEventTypes.TouchEnd);
                domElement.off(DomElementInteractionEventTypes.PointerTap);
                domElement.off(DomElementInteractionEventTypes.PointerAudio);

                if (domElement.element && domElement.element.parentElement) {
                    this.domDiv.removeChild(domElement.element);
                }
                domElement.destroy();
            }
            this.elements = [];
        }
    }

    private getScreenDataByName(screenName: string): DomLayerScreenInterface | undefined {
        let screenData;
        let tempScreenData;
        if (this.manifest) {
            for (let i: number = 0; i < this.manifest.screens.length; i++) {
                tempScreenData = this.manifest.screens[i];
                if (tempScreenData.name === screenName) {
                    screenData = tempScreenData;
                }
            }
        }
        return screenData;
    }

    private getElementDataByName(elementName: string): DomLayerElementInterface | null {
        if (this.manifest.elements) {
            for (let i: number = 0; i < this.manifest.elements.length; i++) {
                const elementData: DomLayerElementInterface = this.manifest.elements[i];
                if (elementData.name === elementName) {
                    return elementData;
                }
            }
        }

        return null;
    }

    private getScreenStateByName(
        screenData: DomLayerScreenInterface,
        stateName: string
    ): DomLayerScreenStateInterface | null {
        if (screenData.states) {
            for (let i: number = 0; i < screenData.states.length; i++) {
                const stateData: DomLayerScreenStateInterface = screenData.states[i];
                if (stateData.name === stateName) {
                    return stateData;
                }
            }
        }
        return null;
    }

    addElement(elementName: string) {
        if (this.currentElementList) {
            if (this.isElement(elementName)) return;

            // get the new element to add
            const elementData = this.getElementDataByName(elementName);

            // destroy the current display and rebuild
            if (elementData) {
                this.destroyAllElements();
                this.currentElementList.push({ name: elementName });
                this.createAllFromElementList(this.currentElementList);
            }
        }
    }

    removeElement(elementName: string) {
        if (this.currentElementList) {
            for (let i = 0; i < this.currentElementList.length; i++) {
                if (this.currentElementList[i].name === elementName) {
                    this.destroyAllElements();
                    this.currentElementList.splice(i, 1);
                    this.createAllFromElementList(this.currentElementList);
                    break;
                }
            }
        }
    }

    isElement(elementName: string): boolean {
        let exists = false;
        if (this.currentElementList) {
            for (let i = 0; i < this.currentElementList.length; i++) {
                if (this.currentElementList[i].name === elementName) {
                    exists = true;
                }
            }
        }
        return exists;
    }

    getElementByName(elementName: string): null | DomElementBase {
        let element = null;
        if (this.elements) {
            for (let i = 0; i < this.elements.length; i++) {
                if (this.elements[i].name === elementName) {
                    element = this.elements[i];
                }
            }
        }
        return element;
    }

    getCurrentElementList(): DomLayerScreenElementInterface[] | null {
        return this.currentElementList;
    }

    private createAllElements(stateName?: string | undefined) {
        let screenData: DomLayerScreenInterface | undefined = this.getScreenDataByName(
            this.currentScreenName
        );
        let stateData: DomLayerScreenStateInterface | null;

        if (screenData) {
            // default screen configuration
            if (typeof stateName === "undefined") {
                // grab the element list once only per screen display
                if (!this.currentElementList && screenData.elements) {
                    this.currentElementList = screenData.elements.slice();
                }
                if (this.currentElementList) {
                    this.createAllFromElementList(this.currentElementList);
                }
            } else {
                stateData = this.getScreenStateByName(screenData, stateName);
                // grab the element list once only per screen display
                if (!this.currentElementList && stateData && stateData.elements) {
                    this.currentElementList = stateData.elements.slice();
                }
                if (this.currentElementList) {
                    this.createAllFromElementList(this.currentElementList);
                }
            }
        }
    }

    private createAllFromElementList(list: DomLayerScreenElementInterface[]) {
        let elementData: DomLayerElementInterface | null;
        for (let i = 0; i < list.length; i++) {
            elementData = this.getElementDataByName(list[i].name);
            if (elementData) {
                this.createElement(elementData);
            }
        }
        this.positionAllElements();
    }

    private createElement(elementData: DomLayerElementInterface) {
        let element: DomElementBase;

        switch (elementData.type) {
            default:
            case DomElementTypes.IMAGE:
                element = new DomElementImage(
                    this,
                    this.baseUrl + this.manifest.imageDirPath,
                    elementData,
                    this.manifest.elementDefaults,
                    this.manifest.elementFrames
                );
                break;
            case DomElementTypes.BUTTON:
                element = new DomElementButton(
                    this,
                    this.baseUrl + this.manifest.imageDirPath,
                    elementData,
                    this.manifest.elementDefaults,
                    this.manifest.elementFrames
                );

                break;
            case DomElementTypes.TOGGLE:
                element = new DomElementToggle(
                    this,
                    this.baseUrl + this.manifest.imageDirPath,
                    elementData,
                    this.manifest.elementDefaults,
                    this.manifest.elementFrames
                );

                break;
        }

        if (
            elementData.type === DomElementTypes.BUTTON ||
            elementData.type === DomElementTypes.TOGGLE
        ) {
            element.on(
                DomElementInteractionEventTypes.MouseOver,
                this.onDomInteractiveMouseOver.bind(this)
            );
            element.on(
                DomElementInteractionEventTypes.MouseDown,
                this.onDomInteractiveMouseDown.bind(this)
            );
            element.on(
                DomElementInteractionEventTypes.MouseUp,
                this.onDomInteractiveMouseUp.bind(this)
            );
            element.on(
                DomElementInteractionEventTypes.Click,
                this.onDomInteractiveClick.bind(this)
            );
            element.on(
                DomElementInteractionEventTypes.TouchStart,
                this.onDomInteractiveTouchStart.bind(this)
            );
            element.on(
                DomElementInteractionEventTypes.TouchEnd,
                this.onDomInteractiveTouchEnd.bind(this)
            );
            element.on(
                DomElementInteractionEventTypes.PointerTap,
                this.onDomInteractivePointerTap.bind(this)
            );
            element.on(
                DomElementInteractionEventTypes.PointerAudio,
                this.onDomInteractivePointerAudio.bind(this)
            );
        }

        this.elements.push(element);
    }

    private onDomInteractiveMouseOver(event: DomElementInteractionEvent) {
        this.emitInteractiveEvent(event, DomElementInteractionEventTypes.MouseOver);
    }

    private onDomInteractiveMouseDown(event: DomElementInteractionEvent) {
        this.emitInteractiveEvent(event, DomElementInteractionEventTypes.MouseDown);
    }

    private onDomInteractiveMouseUp(event: DomElementInteractionEvent) {
        this.emitInteractiveEvent(event, DomElementInteractionEventTypes.MouseUp);
    }

    private onDomInteractiveClick(event: DomElementInteractionEvent) {
        this.emitInteractiveEvent(event, DomElementInteractionEventTypes.Click);
    }

    private onDomInteractiveTouchStart(event: DomElementInteractionEvent) {
        this.emitInteractiveEvent(event, DomElementInteractionEventTypes.TouchStart);
    }

    private onDomInteractiveTouchEnd(event: DomElementInteractionEvent) {
        this.emitInteractiveEvent(event, DomElementInteractionEventTypes.TouchEnd);
    }

    private onDomInteractivePointerTap(event: DomElementInteractionEvent) {
        this.emitInteractiveEvent(event, DomElementInteractionEventTypes.PointerTap);
    }

    private onDomInteractivePointerAudio(event: DomElementInteractionEvent) {
        this.emitInteractiveEvent(event, DomElementInteractionEventTypes.PointerAudio);
    }

    private emitInteractiveEvent(
        event: DomElementInteractionEvent,
        type: DomElementInteractionEventTypes
    ) {
        const domLayerEvent: DomElementInteractionEvent = {
            type: type,
            event: event.event,
            element: event.element,
            toggleState: event.toggleState
        };
        this.emit(type, domLayerEvent);
        if (this.stateChangeAfterEvent) {
            const stateName = this.stateChangeAfterEvent;
            this.stateChangeAfterEvent = null;
            this.updateElementStateVariables(stateName);
        }
    }

    /**
     * Returns a list of elements which are required for the specified location.
     * @param location
     */
    private getLocationList(location: string): DomElementBase[] {
        let list: DomElementBase[] = [];
        for (var i = 0; i < this.elements.length; i++) {
            if (this.elements[i].location === location) {
                list.push(this.elements[i]);
            }
        }
        return list;
    }

    private getLocationNameByIndex(index: number): string {
        switch (index) {
            default:
            case 0:
                return "TopLeft";
            case 1:
                return "TopMiddle";
            case 2:
                return "TopRight";
            case 3:
                return "MiddleLeft";
            case 4:
                return "Middle";
            case 5:
                return "MiddleRight";
            case 6:
                return "BottomLeft";
            case 7:
                return "BottomMiddle";
            case 8:
                return "BottomRight";
        }
    }

    private appendElement(element: DomElementBase) {
        if (element.element && !element.hideElement) {
            this.domDiv.appendChild(element.element);
        }
    }

    private positionAllElements() {
        const perc1: number = Math.round(Math.max(window.innerWidth, window.innerHeight) / 100);
        const padding: number = this.manifest.screenMarginPerc * perc1;
        const halfWidth: number = Math.round(Math.max(window.innerWidth, window.innerHeight) / 2);
        let totalX: number;
        let i: number, j: number;
        let domElement: DomElementBase;

        // go through each of the optional 9 positions
        for (i = 0; i < 9; i++) {
            const locationName: string = this.getLocationNameByIndex(i);
            const startTabIndex: number = (i + 1) * 10;
            let locationList: DomElementBase[] = this.getLocationList(locationName);
            totalX = padding;

            // sort the elements by their location indexes, so that they're added in
            // the correct order, outside, inwards
            locationList.sort(function(a, b) {
                return a.locationIndex - b.locationIndex;
            });

            for (j = 0; j < locationList.length; j++) {
                domElement = locationList[j];
                if (domElement.element) {
                    if (domElement.title !== "") {
                        domElement.element.tabIndex = startTabIndex + j;
                    }
                }
            }

            // split the element list into absolute and relative lists
            let absoluteList = [];
            let relativeList = [];
            //
            for (j = 0; j < locationList.length; j++) {
                if (locationList[j].absolute) {
                    absoluteList.push(locationList[j]);
                } else {
                    relativeList.push(locationList[j]);
                }
            }

            // calculate the position on the x axis
            switch (locationName) {
                case "TopLeft":
                case "MiddleLeft":
                case "BottomLeft":
                    for (j = 0; j < absoluteList.length; j++) {
                        absoluteList[j].xPos = padding;
                    }
                    for (j = 0; j < relativeList.length; j++) {
                        // apply any required extra spacing to subsequent elements
                        if (j > 0) {
                            totalX += Math.max(
                                this.manifest.minElementSpacing - relativeList[j].padding * 2,
                                0
                            );
                        }
                        relativeList[j].xPos = totalX;
                        totalX += relativeList[j].width + relativeList[j].padding * 2;
                    }
                    break;
                case "TopMiddle":
                case "Middle":
                case "BottomMiddle":
                    let spacing: number = 0;
                    for (j = 0; j < absoluteList.length; j++) {
                        absoluteList[j].xPos = halfWidth - absoluteList[j].width / 2;
                    }

                    // calculate any required extra spacing
                    if (relativeList.length > 0) {
                        spacing = Math.max(
                            this.manifest.minElementSpacing - relativeList[0].padding * 2,
                            0
                        );
                    }

                    switch (relativeList.length) {
                        case 1:
                            relativeList[0].xPos =
                                halfWidth -
                                (relativeList[0].width + relativeList[0].padding * 2) / 2;
                            break;

                        case 2:
                            relativeList[0].xPos =
                                halfWidth -
                                relativeList[0].width -
                                relativeList[0].padding * 2 -
                                spacing / 2;
                            relativeList[1].xPos = halfWidth + spacing / 2;
                            break;
                        case 3:
                            relativeList[0].xPos =
                                halfWidth -
                                (relativeList[1].width + relativeList[1].padding * 2) / 2 -
                                (relativeList[0].width + relativeList[0].padding * 2) -
                                spacing;
                            relativeList[1].xPos =
                                halfWidth -
                                (relativeList[1].width + relativeList[1].padding * 2) / 2;
                            relativeList[2].xPos =
                                halfWidth +
                                (relativeList[1].width + relativeList[1].padding * 2) / 2 +
                                spacing;
                            break;
                    }
                    break;
                case "TopRight":
                case "MiddleRight":
                case "BottomRight":
                    for (j = 0; j < absoluteList.length; j++) {
                        absoluteList[j].xPos = padding;
                    }
                    for (j = relativeList.length - 1; j >= 0; j--) {
                        // apply any required extra spacing to subsequent elements
                        if (j < relativeList.length - 1) {
                            totalX += Math.max(
                                this.manifest.minElementSpacing - relativeList[j].padding * 2,
                                0
                            );
                        }
                        relativeList[j].xPos = totalX;

                        // add the width of the current element
                        totalX += relativeList[j].width + relativeList[j].padding * 2;
                    }
                    break;
            }

            // merge the absolute and relative lists back into one, with the absolute first,
            // so they always sit underneath any relative elements
            locationList = absoluteList.concat(relativeList);
            // position on the y axis
            switch (locationName) {
                case "TopLeft":
                case "TopMiddle":
                case "TopRight":
                    for (j = 0; j < locationList.length; j++) {
                        domElement = locationList[j];
                        const offset: number = this.smallerThanBreakpoint
                            ? domElement.offset.small.y
                            : domElement.offset.large.y;
                        if (domElement.element) {
                            domElement.element.style.top = padding + offset + "px";
                        }
                    }
                    break;
                case "MiddleLeft":
                case "Middle":
                case "MiddleRight":
                    for (j = 0; j < locationList.length; j++) {
                        domElement = locationList[j];
                        const offset: number = this.smallerThanBreakpoint
                            ? domElement.offset.small.y
                            : domElement.offset.large.y;
                        if (domElement.element) {
                            domElement.element.style.top = `calc(50% - ${(domElement.height +
                                domElement.padding * 2) /
                                2 -
                                offset}px)`;
                        }
                    }
                    break;
                case "BottomLeft":
                case "BottomMiddle":
                case "BottomRight":
                    for (j = 0; j < locationList.length; j++) {
                        domElement = locationList[j];
                        const offset: number = this.smallerThanBreakpoint
                            ? domElement.offset.small.y
                            : domElement.offset.large.y;
                        if (domElement.element) {
                            domElement.element.style.bottom = padding - offset + "px";
                        }
                    }
                    break;
            }

            // position on the x axis and add to the dom
            // this is carried out separately to the calculation of the x position,
            // which allows elements to be added in reverse order, if required
            switch (locationName) {
                case "TopLeft":
                case "MiddleLeft":
                case "BottomLeft":
                    for (j = 0; j < locationList.length; j++) {
                        domElement = locationList[j];
                        const offset: number = this.smallerThanBreakpoint
                            ? domElement.offset.small.x
                            : domElement.offset.large.x;
                        if (domElement.element) {
                            domElement.element.style.left = domElement.xPos + offset + "px";
                            this.appendElement(domElement);
                        }
                    }
                    break;
                case "TopMiddle":
                case "Middle":
                case "BottomMiddle":
                    for (j = 0; j < locationList.length; j++) {
                        domElement = locationList[j];
                        const offset: number = this.smallerThanBreakpoint
                            ? domElement.offset.small.x
                            : domElement.offset.large.x;
                        if (domElement.element) {
                            domElement.element.style.left = domElement.xPos + offset + "px";
                            this.appendElement(domElement);
                        }
                    }
                    break;
                case "TopRight":
                case "MiddleRight":
                case "BottomRight":
                    for (j = 0; j < locationList.length; j++) {
                        domElement = locationList[j];
                        const offset: number = this.smallerThanBreakpoint
                            ? domElement.offset.small.x
                            : domElement.offset.large.x;
                        if (domElement.element) {
                            domElement.element.style.right = domElement.xPos - offset + "px";
                            this.appendElement(domElement);
                        }
                    }
                    break;
            }
        }

        DomLayer.sortNodes(this.domDiv);
    }
}
